// Saringan layar Laporan: bentuknya, cara mengubahnya menjadi query string, dan
// ringkasannya untuk manusia.
//
// ARTI tiap saringan (apa yang cocok dengan "edi", siapa yang huruf awalnya
// "E", apa itu "data identitas lengkap") TIDAK didefinisikan di sini. Server
// yang menyaring (gladhy-backend: services/reportFilter.service.js), dan layar
// memakai baris hasil saringan server — dengan begitu tabel di layar dan berkas
// yang diunduh tidak bisa berbeda isi. Modul ini hanya mengemas pilihan admin.

export const SARINGAN_KOSONG = Object.freeze({
  q: '', huruf: '', perusahaan: '', status: '', halangan: '', tes: '', hadir: '', identitas: '',
  // A–Z sebagai urutan awal: daftar ratusan nama dicari menurut abjad, bukan
  // menurut urutan pendaftarannya.
  urut: 'nama', arah: 'asc',
})

// Saringan tab Kehadiran Sesi: urutan awal kronologis, seperti sebelumnya.
export const SARINGAN_SESI_KOSONG = Object.freeze({
  q: '', dari: '', sampai: '', ditandai: '', urut: 'jadwal', arah: 'asc',
})
// Saringan tab Progres per Course: tanpa urutan berarti urutan course yang
// disusun admin (order_index), seperti sebelumnya.
export const SARINGAN_COURSE_KOSONG = Object.freeze({ q: '', urut: '', arah: '' })

const KUNCI_PESERTA = ['q', 'huruf', 'perusahaan', 'status', 'halangan', 'tes', 'hadir', 'identitas']
const KUNCI_SESI = ['q', 'dari', 'sampai', 'ditandai']
// Kunci yang mengurangi baris (urutan tidak termasuk).
export const KUNCI_SARING = [...KUNCI_PESERTA, 'dari', 'sampai', 'ditandai']

// Kunci yang diterima tiap jenis laporan di server. Kunci lain DITOLAK server
// (400), jadi yang dikirim harus persis yang berlaku untuk berkas itu.
export const KUNCI_JENIS = {
  progress: [...KUNCI_PESERTA, 'urut', 'arah'],
  imo: [...KUNCI_PESERTA.filter((k) => k !== 'halangan'), 'urut', 'arah'],
  // jawaban tes dan matriks kehadiran: tanpa nilai dan status sertifikat
  orang: ['q', 'huruf', 'perusahaan', 'urut', 'arah'],
  sesi: [...KUNCI_SESI, 'urut', 'arah'],
  // rincian pemindaian disaring menurut SESInya, sama dengan rekap per sesi
  pemindaian: [...KUNCI_SESI, 'urut', 'arah'],
  course: ['q', 'urut', 'arah'],
}
// Urutan yang dikenal tiap jenis; urutan lain tidak dikirim (berkasnya lalu
// memakai urutan bawaannya sendiri).
const URUT_JENIS = {
  progress: ['nama', 'perusahaan', 'pretest', 'posttest', 'gain', 'kehadiran', 'sertifikat'],
  imo: ['nama', 'perusahaan', 'pretest', 'posttest', 'gain', 'kehadiran', 'sertifikat'],
  orang: ['nama', 'perusahaan'],
  sesi: ['jadwal', 'judul', 'hadir', 'ditandai'],
  pemindaian: ['jadwal', 'nama', 'waktu'],
  course: ['judul', 'selesai'],
}

/** "dari" setelah "sampai": server menolaknya, jadi tidak dikirim sama sekali. */
export const rentangSalah = (s) => Boolean(s?.dari && s?.sampai && s.dari > s.sampai)

export const HURUF = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#']

/** Parameter query untuk satu jenis laporan: hanya kunci yang berlaku dan terisi. */
export function keQuery(saringan, jenis) {
  const kunci = KUNCI_JENIS[jenis]
  const hasil = {}
  for (const k of kunci) {
    const v = typeof saringan?.[k] === 'string' ? saringan[k].trim() : ''
    if (v) hasil[k] = v
  }
  if (hasil.urut && !URUT_JENIS[jenis].includes(hasil.urut)) {
    delete hasil.urut
    delete hasil.arah
  }
  if (!hasil.urut) delete hasil.arah
  return hasil
}

/** Saringan aktif (yang mengurangi baris) yang TIDAK ikut ke berkas `jenis`. */
export function tidakIkut(saringan, jenis) {
  return KUNCI_SARING.filter((k) => saringan?.[k] && !KUNCI_JENIS[jenis].includes(k))
}

export const jumlahAktif = (saringan) => KUNCI_SARING.filter((k) => saringan?.[k]).length

/**
 * Hitungan nama per huruf awal, dari bidang `huruf` yang dikirim server.
 * `null` bila barisnya tidak membawa bidang itu (server lama) — deretan huruf
 * lalu disembunyikan, alih-alih menebak definisi "huruf awal" di sini.
 */
export function hitungHuruf(rows) {
  if (!rows.length || !rows.every((r) => typeof r.huruf === 'string')) return null
  const m = new Map(HURUF.map((h) => [h, 0]))
  for (const r of rows) m.set(r.huruf, (m.get(r.huruf) || 0) + 1)
  return m
}

const LABEL_HALANGAN = {
  POSTTEST_MISSING: ['Posttest belum dikerjakan', 'Posttest not taken'],
  POSTTEST_LOW: ['Posttest di bawah ambang', 'Posttest below threshold'],
  ATTENDANCE_LOW: ['Kehadiran di bawah ambang', 'Attendance below threshold'],
  NOT_CONFIRMED: ['Menunggu konfirmasi sistem', 'Awaiting system confirmation'],
}
export const PILIHAN_HALANGAN = Object.keys(LABEL_HALANGAN)

/** Satu label per saringan aktif, untuk chip dan ringkasan menu Unduh. */
export function labelAktif(saringan, L) {
  const t = (pasangan) => L(pasangan[0], pasangan[1])
  const s = saringan || {}
  const hasil = []
  if (s.q) hasil.push({ kunci: 'q', label: L(`Cari "${s.q}"`, `Search "${s.q}"`) })
  if (s.huruf) hasil.push({ kunci: 'huruf', label: s.huruf === '#' ? L('Awal bukan huruf', 'Non-letter start') : L(`Huruf ${s.huruf}`, `Letter ${s.huruf}`) })
  if (s.perusahaan) hasil.push({ kunci: 'perusahaan', label: s.perusahaan })
  if (s.status) hasil.push({ kunci: 'status', label: s.status === 'layak' ? L('Layak sertifikat', 'Cert. eligible') : L('Belum layak', 'Not yet eligible') })
  if (s.halangan) hasil.push({ kunci: 'halangan', label: t(LABEL_HALANGAN[s.halangan] || [s.halangan, s.halangan]) })
  if (s.tes) hasil.push({ kunci: 'tes', label: s.tes === 'belum_pretest' ? L('Belum pretest', 'No pretest') : L('Belum posttest', 'No posttest') })
  if (s.hadir) hasil.push({ kunci: 'hadir', label: L('Kehadiran di bawah ambang', 'Attendance below threshold') })
  if (s.identitas) hasil.push({ kunci: 'identitas', label: L('Data identitas belum lengkap', 'Identity data incomplete') })
  if (s.dari) hasil.push({ kunci: 'dari', label: L(`Sejak ${s.dari}`, `From ${s.dari}`) })
  if (s.sampai) hasil.push({ kunci: 'sampai', label: L(`Sampai ${s.sampai}`, `Until ${s.sampai}`) })
  if (s.ditandai) hasil.push({ kunci: 'ditandai', label: L('Hanya yang ditandai', 'Flagged only') })
  return hasil
}

export const labelHalangan = (kode, L) => {
  const p = LABEL_HALANGAN[kode]
  return p ? L(p[0], p[1]) : kode
}

// ── Simpan per cakupan (program + kelas) dan tab, hanya untuk sesi ini ──────
// sessionStorage, bukan localStorage: saringan yang diam-diam masih aktif saat
// admin kembali besok, lalu mengunduh "laporan" yang ternyata sebagian, adalah
// kejutan yang mahal.
//
// Per KELAS, bukan hanya per program: perusahaan dan huruf yang dipilih milik
// daftar peserta kelas itu. Dan karena konteks kelas bisa dipulihkan sesaat
// setelah halaman dimuat, menyimpannya per kelas membuat pemulihan itu
// memuat saringan yang benar, alih-alih menghapusnya.
export const lingkupSaringan = (programId, classId) => `${programId}:${classId || 'semua'}`
const kunciSimpan = (lingkup, tab) => `gladhy_laporan_saringan:${lingkup}:${tab}`

export function bacaSaringan(lingkup, tab, kosong = SARINGAN_KOSONG) {
  try {
    const mentah = sessionStorage.getItem(kunciSimpan(lingkup, tab))
    if (!mentah) return { ...kosong }
    const data = JSON.parse(mentah)
    const hasil = { ...kosong }
    for (const k of Object.keys(kosong)) {
      if (typeof data?.[k] === 'string') hasil[k] = data[k]
    }
    return hasil
  } catch {
    return { ...kosong }
  }
}

export function simpanSaringan(lingkup, tab, saringan) {
  try {
    sessionStorage.setItem(kunciSimpan(lingkup, tab), JSON.stringify(saringan))
  } catch {
    // Penyimpanan diblokir (mode privat): saringan tetap berlaku sampai halaman dimuat ulang.
  }
}
